// Firebase v10 modular SDK
import { initializeApp, getApps, getApp } from 'firebase/app';
import type { FirebaseApp, FirebaseOptions } from 'firebase/app';
import { connectAuthEmulator, getAuth, onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import log from './log';
import { getEnvVar, getFirebaseConfig } from '../utils/env';

const firebaseConfig = getFirebaseConfig();

// Initialize Firebase app
let app: FirebaseApp;
try {
  if (getApps().length === 0) {
    log.info('[Firebase Config] Initializing Firebase with config...');
    app = initializeApp(firebaseConfig as FirebaseOptions);
    log.info('[Firebase Config] Firebase initialized successfully');
  } else {
    log.info('[Firebase Config] Firebase already initialized, using existing app');
    app = getApp();
  }
} catch (err) {
  const error = err as { message?: string; code?: string; stack?: string };
  log.error('[Firebase Config] Firebase initialization failed:', error);
  log.error('[Firebase Config] Error details:', {
    message: error.message,
    code: error.code,
    stack: error.stack,
  });
  throw err;
}

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

type GlobalWithEmulators = typeof globalThis & { _firebaseEmulatorsConnected?: boolean };

// Initialize emulators if specified
if (getEnvVar('VITE_USE_FIREBASE_EMULATOR') === 'true') {
  const g = globalThis as GlobalWithEmulators;
  if (!g._firebaseEmulatorsConnected) {
    try {
      connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
      log.info('Connected to Auth Emulator');
    } catch (err) {
      const e = err as { message?: string };
      if (!e.message?.includes('already called')) {
        log.error('Auth emulator connection failed:', e.message);
        throw err;
      }
    }

    try {
      connectFirestoreEmulator(db, 'localhost', 8083);
      log.info('Connected to Firestore Emulator');
    } catch (err) {
      const e = err as { message?: string };
      if (!e.message?.includes('already called') && !e.message?.includes('already been started')) {
        log.error('Firestore emulator connection failed:', e.message);
        throw err;
      }
    }

    g._firebaseEmulatorsConnected = true;
    log.info('Using Local Emulator');
  }
}

export const getIdToken = async (): Promise<string> => {
  const authInstance = getAuth();

  const user = await new Promise<User | null>((resolve) => {
    const unsubscribe = onAuthStateChanged(authInstance, (u) => {
      unsubscribe();
      resolve(u);
    });
  });

  if (!user) {
    throw new Error('User Not Authenticated');
  }

  return user.getIdToken(false);
};

export const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const idToken = await getIdToken();
  return {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
};

export default app;
export { app };
